'use client';

import { captureException } from '@sentry/nextjs';
import { FC, useEffect, useState } from 'react';

import { CARMA_PHONE_NUMBER, CARMA_PHONE_NUMBER_PRETTY } from 'src/constants/constants';
import { Button } from 'src/general/components/Button/Button';
import { StepFormHeader } from 'src/steps-form/components/StepFormHeader/StepFormHeader';
import { StepFormSetPreventExit } from 'src/steps-form/stepFormStore';
import { ErrorPageProps } from 'src/types/appRouter.types';

type Props = {
  exceptionDescription: string;
} & ErrorPageProps;

export const StepFormError: FC<Props> = ({ exceptionDescription, error, reset }) => {
  const [clicked, setClicked] = useState<boolean>(false);

  useEffect(() => {
    // Log the error to an error reporting service
    console.error(exceptionDescription, error);
    captureException(error, {
      level: 'error',
      tags: { context: 'StepFormError', digest: error.digest },
      extra: { description: exceptionDescription },
    });
  }, [error, exceptionDescription]);

  return (
    <>
      <StepFormSetPreventExit preventExit={false} />
      <StepFormHeader title="Oops, looks like something went wrong." />
      <div className="RichText">
        <div className="RichText-content">
          <p>
            It looks like there’s a temporary issue on our end that prevented your submission from going through. Please
            try again shortly.
          </p>
          <p>
            If the problem persists, feel free to call our team on{' '}
            <a href={`tel:${CARMA_PHONE_NUMBER}`}>{CARMA_PHONE_NUMBER_PRETTY}</a> or use the chat widget.
            <br />
            Apologies for the inconvenience!
          </p>
          <Button
            onClick={() => {
              setClicked(true);
              reset();
            }}
            isLoading={clicked}
            isDisabledWhileLoading
          >
            Try again
          </Button>
        </div>
      </div>
    </>
  );
};
